/**
 * Phase 32 / NAV-01 + NAV-04: the single typed nav config that drives the sidenav.
 *
 * The shell (`app.component.html`) iterates this config instead of carrying
 * scattered per-item `@if (canSee(...))` gates. Adding, gating, or reordering a
 * nav item is now a ONE-PLACE data edit here; visibility is driven solely by each
 * item's `roles` array (evaluated via UserService.hasAnyRole, case-insensitive).
 *
 * Group/role presets derive from the UI-SPEC per-role preset matrix
 * (§Navigation IA Contract) and the existing `canSee(...)` gates in the old
 * app.component.html.
 *
 * 2026-06-23 consolidation: the standalone **Records** and **Upload** nav items were
 * retired. Records live on the submission "Submitted data" tab, and Upload is reached
 * via the Submissions page action — everything is now reached through a submission, so
 * the nav collapses to Work (Home · Submissions · Approvals) / Administer / Settings.
 *
 * 2026-06-24 (quick-260624-bed): the standalone **Collections** nav item is RESTORED in the
 * Work group — the cross-file records browser is once again reachable from the sidenav now
 * that the submission page's "Submitted data" tab is retired. **Upload** stays retired
 * (reached via the Uploads page action).
 *
 * 2026-06-24 (quick-260624-hwv): Work-group label rename only (routes unchanged) — the
 * former **Records** item now reads **Collections** (route /records) and the former
 * **Submissions** item now reads **Uploads** (route /submissions). The Administer-group
 * **Collections** item (route /admin/collections) is a DIFFERENT surface — untouched.
 *
 * 2026-10-12 (Phase 47 D-15): **Dashboards** (route /dashboards, embedded Looker) added
 * directly after Home for every role. The API's 403 is the real scope gate; unprovisioned
 * users have no roles, so the item stays hidden for them.
 */

export type NavGroup = 'work' | 'administer' | 'settings';

export interface NavItem {
  group: NavGroup;
  label: string;
  /** mat-icon (Material Symbol) name. */
  icon: string;
  /** Absolute route. `/records` is resolved per-role by RecordsLandingService. */
  route: string;
  /** Visibility gate — the item renders only if the user has ANY of these roles. */
  roles: readonly string[];
}

export const NAV_GROUPS: readonly NavItem[] = [
  // Work
  {
    group: 'work',
    label: 'Home',
    icon: 'dashboard',
    route: '/dashboard',
    roles: [
      'PLATFORM_ADMIN',
      'STATE_ADMIN',
      'DISTRICT_ADMIN',
      'SCHOOL_ADMIN',
      'SCHOOL_USER',
      'REVIEWER',
      'DATA_STEWARD',
      'VIEWER',
    ],
  },
  // Embedded Looker dashboards (Phase 47). Not to be confused with Home's /dashboard route.
  {
    group: 'work',
    label: 'Dashboards',
    icon: 'insights',
    route: '/dashboards',
    roles: [
      'PLATFORM_ADMIN',
      'STATE_ADMIN',
      'DISTRICT_ADMIN',
      'SCHOOL_ADMIN',
      'SCHOOL_USER',
      'REVIEWER',
      'DATA_STEWARD',
      'VIEWER',
    ],
  },
  // Submissions is the hub: upload, view accumulated records (the "Submitted data"
  // tab), and submit — all reached through a submission. The standalone Records and
  // Upload nav items were retired (consolidated into the submission page).
  {
    group: 'work',
    label: 'Uploads',
    icon: 'folder',
    route: '/submissions',
    // All roles EXCEPT DATA_STEWARD.
    roles: [
      'PLATFORM_ADMIN',
      'STATE_ADMIN',
      'DISTRICT_ADMIN',
      'SCHOOL_ADMIN',
      'SCHOOL_USER',
      'REVIEWER',
      'VIEWER',
    ],
  },
  // Records restored 2026-06-24 (quick-260624-bed): the cross-file accumulated
  // records browser, reachable from the sidenav again. `/records` is resolved
  // per-role by RecordsLandingService. Mirror Submissions visibility (all roles
  // EXCEPT DATA_STEWARD).
  {
    group: 'work',
    label: 'Collections',
    icon: 'table_rows',
    route: '/records',
    roles: [
      'PLATFORM_ADMIN',
      'STATE_ADMIN',
      'DISTRICT_ADMIN',
      'SCHOOL_ADMIN',
      'SCHOOL_USER',
      'REVIEWER',
      'VIEWER',
    ],
  },
  {
    group: 'work',
    label: 'Approvals',
    icon: 'verified',
    route: '/approvals',
    roles: ['SCHOOL_ADMIN', 'DISTRICT_ADMIN', 'STATE_ADMIN', 'PLATFORM_ADMIN', 'REVIEWER'],
  },

  // Administer
  {
    group: 'administer',
    label: 'Collections',
    icon: 'collections_bookmark',
    route: '/admin/collections',
    roles: ['STATE_ADMIN', 'PLATFORM_ADMIN'],
  },
  {
    group: 'administer',
    label: 'Users',
    icon: 'group_add',
    route: '/admin/users',
    roles: ['PLATFORM_ADMIN', 'STATE_ADMIN', 'DISTRICT_ADMIN', 'SCHOOL_ADMIN'],
  },
  {
    group: 'administer',
    label: 'Org hierarchy',
    icon: 'account_tree',
    route: '/admin/org',
    roles: ['STATE_ADMIN', 'PLATFORM_ADMIN', 'DISTRICT_ADMIN'],
  },
  {
    group: 'administer',
    label: 'Identity exceptions',
    icon: 'person_search',
    route: '/admin/idr/exceptions',
    roles: ['STATE_ADMIN', 'PLATFORM_ADMIN', 'DATA_STEWARD'],
  },

  // Settings
  {
    group: 'settings',
    label: 'Notifications',
    icon: 'notifications',
    route: '/settings/notifications',
    // Notification preferences are available to every authenticated role.
    roles: [
      'PLATFORM_ADMIN',
      'STATE_ADMIN',
      'DISTRICT_ADMIN',
      'SCHOOL_ADMIN',
      'SCHOOL_USER',
      'REVIEWER',
      'DATA_STEWARD',
      'VIEWER',
    ],
  },
];

/** Ordered group render sequence + the section header shown above each group. */
export const NAV_GROUP_ORDER: readonly { group: NavGroup; header: string }[] = [
  { group: 'work', header: 'Work' },
  { group: 'administer', header: 'Administer' },
  { group: 'settings', header: 'Settings' },
];
